import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button } from 'reactstrap';
import { useGetDocumentConnectionsQuery } from '@api/utrv';
import { useConnectionContext } from './ConnectionContext';
import { useAssistantPanelContext } from '../AssistantPanelContext';
import { AssistantTabs } from '../assistantTypes';
import { useSmartMatchHints } from './useSmartMatchHints';
import { SMART_MATCH } from '@constants/terminology';
import { loggerMessage } from '../../../logger';
import { BannerAlert, VariantType } from '@g17eco/molecules/alert/BannerAlert';

export const SmartMatchesBanner = () => {
  const { utrvId } = useConnectionContext();
  const { openToTab } = useAssistantPanelContext();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(false);
  }, [utrvId]);

  const queryArg = utrvId ? { utrvId } : skipToken;
  const hints = useSmartMatchHints(utrvId);
  const {
    currentData: documentData,
    isFetching: isFetchingDocuments,
    isError: isDocumentError,
  } = useGetDocumentConnectionsQuery(queryArg);

  useEffect(() => {
    if (isDocumentError) {
      loggerMessage('Failed to fetch document connections for smart match banner', { level: 'error' });
    }
  }, [isDocumentError]);

  const totalCount = useMemo(() => {
    const { connections: documentConnections = [] } = documentData ?? {};
    return hints.length + documentConnections.length;
  }, [hints, documentData]);

  if (isDismissed || isFetchingDocuments || !utrvId || totalCount === 0) {
    return null;
  }

  return (
    <BannerAlert className='mt-3 mb-3' variant={VariantType.Auto} handleClose={() => setIsDismissed(true)}>
      <i className='fa-solid fa-circle-info text-ThemeAccentDark' style={{ lineHeight: 1 }} />
      <span className='text-ThemeAccentDark'>
        <strong>{totalCount} {totalCount === 1 ? SMART_MATCH.CAPITALIZED_SINGULAR : SMART_MATCH.CAPITALIZED_PLURAL} found:</strong> We found potentially relevant data for this metric
      </span>
      <Button
        size='sm'
        outline
        color='primary'
        className='text-nowrap rounded'
        onClick={() => openToTab(AssistantTabs.SmartMatches)}
      >
        View matches
      </Button>
    </BannerAlert>
  );
};
